// ============================================
// Operations Manager Dashboard Page
// ============================================

import { useState, useEffect } from 'react';
import AppShell from '../layout/AppShell';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../lib/api';

function StatCard({ label, value, icon, sub }: { label: string; value: string | number; icon: string; sub?: string }) {
  return (
    <div className="glass-card stat-card animate-float-in">
      <div className="stat-card__label">{icon} {label}</div>
      <div className="stat-card__value">{value}</div>
      {sub && <div className="stat-card__footer">{sub}</div>}
    </div>
  );
}

export default function OperationsManagerDashboardPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [teams, setTeams] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [td, ud] = await Promise.all([api.get<any>('/teams'), api.get<any>('/users')]);
      setTeams(td.teams || []);
      setUsers(ud.users || []);
    } catch {}
    setLoading(false);
  };

  const renderDashboard = () => (
    <>
      <div className="grid-stats" style={{ marginBottom: 'var(--space-6)' }}>
        <StatCard label="Total Teams" value={teams.length} icon="🏢" />
        <StatCard label="Total Users" value={users.length} icon="👥" />
        <StatCard label="Active Users" value={users.filter((u: any) => u.isActive).length} icon="✅" sub="Currently active" />
        <StatCard label="System Status" value="Online" icon="🟢" sub="All services operational" />
      </div>

      <div className="grid-cards">
        <div className="glass-card" style={{ padding: 'var(--space-5)' }}>
          <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, marginBottom: 'var(--space-4)' }}>
            📋 Teams Overview
          </h2>
          {teams.length === 0 ? (
            <div className="empty-state"><div className="empty-state__icon">🏢</div><div className="empty-state__title">No teams yet</div></div>
          ) : (
            <div className="glass-table-wrapper">
              <table className="glass-table">
                <thead><tr><th>Team</th><th>Manager</th><th>Members</th><th>Status</th></tr></thead>
                <tbody>
                  {teams.map((t: any) => (
                    <tr key={t.id}>
                      <td data-label="Team" style={{ fontWeight: 600 }}>{t.name}</td>
                      <td data-label="Manager" style={{ color: 'var(--text-secondary)' }}>{t.managerName || '—'}</td>
                      <td data-label="Members"><span className="glass-badge glass-badge--open">{t.memberCount || 0}</span></td>
                      <td data-label="Status"><span className={`glass-badge glass-badge--${t.isActive ? 'success' : 'closed'}`}>{t.isActive ? 'Active' : 'Inactive'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="glass-card" style={{ padding: 'var(--space-5)' }}>
          <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, marginBottom: 'var(--space-4)' }}>
            👥 Role Distribution
          </h2>
          {(['architect', 'operations_manager', 'team_leader', 'queue_manager', 'sme', 'premier_engineer'] as const).map((role) => {
            const count = users.filter((u: any) => u.role === role).length;
            const pct = users.length > 0 ? (count / users.length) * 100 : 0;
            return (
              <div key={role} className="rating-bar" style={{ marginBottom: 'var(--space-3)' }}>
                <div className="rating-bar__label" style={{ width: 140, fontSize: 'var(--font-size-xs)' }}>
                  {role.replace(/_/g, ' ')}
                </div>
                <div className="rating-bar__track">
                  <div className="rating-bar__fill rating-bar__fill--top" style={{ width: `${pct}%`, background: 'linear-gradient(90deg, rgba(220,20,60,0.6), rgba(220,20,60,0.9))' }} />
                </div>
                <div className="rating-bar__count">{count}</div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );

  const renderTeams = () => (
    <div className="glass-card" style={{ padding: 'var(--space-5)' }}>
      <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, marginBottom: 'var(--space-4)' }}>Teams Management</h2>
      <div className="grid-cards">
        {teams.map((t: any) => (
          <div key={t.id} className="glass-card glass-card--hover" style={{ padding: 'var(--space-5)' }}>
            <div className="flex-between" style={{ marginBottom: 'var(--space-2)' }}>
              <h3 style={{ fontWeight: 700 }}>{t.name}</h3>
              <span className={`glass-badge glass-badge--${t.isActive ? 'success' : 'closed'}`}>{t.isActive ? 'Active' : 'Inactive'}</span>
            </div>
            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)', marginBottom: 'var(--space-3)' }}>{t.description || 'No description'}</div>
            <div className="flex-between">
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>👤 Manager: {t.managerName || 'Unassigned'}</span>
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>👥 {t.memberCount || 0} members</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderContent = () => {
    if (loading) return <div className="flex-center" style={{ padding: 'var(--space-16)' }}><div className="spinner spinner--lg" /></div>;
    switch (activeTab) {
      case 'teams': return renderTeams();
      default: return renderDashboard();
    }
  };

  return (
    <AppShell role="operations_manager" activeTab={activeTab} onTabChange={setActiveTab}>
      {renderContent()}
    </AppShell>
  );
}
